import React, { useMemo } from 'react';
import { PropTypes as PT } from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  searchSelectors,
  currentUserSelectors,
  directoryActions
} from '@lifesize/nucleus';
import SearchMultiSelect from 'components/SearchMultiSelect/SearchMultiSelect';
import PillComp from 'components/Pill/Pill';
import _isEmpty from 'lodash/isEmpty';
import _reject from 'lodash/reject';
import classes from './MultiModeratorSearch.module.scss';
import { FormattedMessage } from 'react-intl';
import Tooltip from 'components/Tooltip/Tooltip';
import SearchDropdownMenu from 'components/SearchDropdown/SearchDropdownMenu';
import ModeratorSearchResults from 'components/ModeratorSearch/ModeratorSearchResults';
import _get from 'lodash/get';

const MultiModeratorSearch = (props) => {
  const { input, meta } = props;
  const dispatch = useDispatch();
  const selfResult = useSelector(currentUserSelectors.myInfo);
  let searchContacts = useSelector(searchSelectors.searchModerators);
  const selectedContactIndex = useSelector((state) => _get(state, 'app.dropdown.selectionIndex'));

  // Add self at top of list
  if (!_isEmpty(selfResult)) {
    searchContacts = _reject(searchContacts, { contact: { id: selfResult.id } });
    searchContacts.unshift({ contact: selfResult });
  }
  // Remove any selected contacts from search results
  searchContacts = useMemo(() => {
    const selectedIds = input.value.filter(c => !!c?.id).map(c => c.id);
    return searchContacts.filter(c => !selectedIds.includes(c.contact.id));
  }, [input.value, searchContacts]);

  const handleSearch = (searchString) => dispatch(directoryActions.search(searchString));

  const handleOnChange = (contacts) => {
    input.onChange(contacts);
  }

  return (
    <fieldset className={classes.container}>
      <label>
        <FormattedMessage
          id="components.contacts.meetingModal.multiModerator.label"
          defaultMessage="Moderator ({count})"
          values={{
            count: input.value ? input.value.length : 0
          }}
        />
      </label>
      <SearchMultiSelect
        handleSearch={handleSearch}
        initialSelection={input.value ? input.value : [selfResult]}
        searchResults={searchContacts}
        onChange={handleOnChange}
        renderSelectedItem={(item, searchSelection, setSearchSelection) => !!item?.name && (
          <PillComp
            key={item.id || item.uuid}
            disabled={false}
            id={item.id || item.uuid}
            onRemove={() => {
              const id = item.id || item.uuid;
              const newSelection = searchSelection.filter(c => c.id ? c.id !== id : c.uuid !== id);
              setSearchSelection(newSelection);
              handleOnChange(newSelection);
            }}
            value={{
              ...item,
              style: { marginLeft: '3px' }
            }}
          >
            <span className={classes.pillLabel}>{item.name}</span>
          </PillComp>
        )}
        renderSearchResults={(handleResultSelect, searchString, searchContainerRef) => (
          <SearchDropdownMenu
            buttonRef={searchContainerRef}
            styles={{
              top: `${searchContainerRef.current.clientHeight + 8}px`,
              left: '10px'
            }}
            maxItems={5}
            clickOutsideHandler={() => null}
            menuComponent={(
              <ModeratorSearchResults
                searchContacts={searchContacts || []}
                searchResults={searchContacts || []}
                allContacts={[]}
                maxItems={searchContacts && searchContacts.length ? Math.min(searchContacts.length, 5) : 1}
                searchString={searchString}
                handleSelectContact={handleResultSelect}
                handleEnter={handleResultSelect}
                selectedContact={_get(searchContacts, `[${selectedContactIndex}].contact`)}
                selectionIndex={selectedContactIndex}
              />
            )}
          />
        )}
      />
      {(input.value || (meta.touched && !meta.active)) && meta.error && (
        <div className={classes.errorBox}>
          <span>{meta.error}</span>
        </div>
      )}
      <Tooltip
        label={
          <FormattedMessage
            id="components.moderatorSearch.tooltip"
            defaultMessage="Moderators can mute and unmute participants, and remove them from meetings."
          />
        }
        location="right"
        style={{
          textAlign: 'left',
          whiteSpace: 'normal',
          width: '170px'
        }}
      />
    </fieldset>
  );
};

MultiModeratorSearch.propTypes = {
  input: PT.object.isRequired,
  meta: PT.object.isRequired
};

export default MultiModeratorSearch;
